import {
  Box,
  Flex,
  Text,
  Tooltip,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Divider
} from '@chakra-ui/react';
import {
  faBarsFilter,
  faDeleteRight,
  faFloppyDisk,
  faFilterList,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonText,
  ClearButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  SearchInput,
  useToast
} from '@morressier/alembic';
import {
  IDocumentType,
  IAdvancedSearchCriterion,
  ISavedFilterSets,
  ISubmissionWorkflowState
} from '@publishing-engine/domain/models';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import {
  fetchWorkspaceTags,
  fetchSubmissionFilters,
  fetchWorkspaceSubmissions,
  IWorkspaceSubmissionFilter,
  fetchSubmissionsDashBoardConfig
} from 'src/api/queries';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import {
  EDITOR_SUBMISSION_LIST_PHASE_MAP,
  SUBMISSION_TYPE_MAP
} from 'src/constants';
import {
  formatCriterionValue,
  parseQueryParams,
  SubmissionNavContext,
  updateQueryParams
} from 'src/utils/submissionsList';
import SubmissionLoader from './SubmissionLoader';
import SavedFiltersModal from './SavedFiltersModal';
import ManageFiltersModal from './ManageFiltersModal';
import SaveFilterText from './SaveFilterText';
import { useNavigate } from 'react-router-dom';
import { SubmissionTags } from './SubmissionTags';
import { SubmissionAuthors } from './SubmissionAuthors';
import { useWorkspace } from '../SubmissionContexts/Users/userHook';
import ScrollToTopButton from '../../public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import FilterDrawer from '../../public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import SortMenu, {
  SortField
} from '../../public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import { formatDate } from 'src/utils/date';
import SubmissionActionMenu from './SubmissionActionMenu';
import Footer from '../../../../components/Footer';
import AnomalyDetectionIcon from 'src/components/AnomalyDetectionIcon';
import AdvancedSearch from './AdvancedSearch';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

const defaultValues: IWorkspaceSubmissionFilter = {
  workflowPhase: [],
  journalId: [],
  documentType: [],
  assigneeId: [],
  tagId: [],
  searchParams: '',
  focusIssueId: [],
  advancedSearch: []
};

const avatarBackgroundByRoleLevel: Record<number, string> = {
  1: '#4C5FD5',
  2: '#2F8F7A',
  3: '#C97A3D'
};
export type filtersItem =
  | 'workflowPhase'
  | 'journalId'
  | 'documentType'
  | 'assigneeId'
  | 'tagId'
  | 'focusIssueId';
/** Kept in step with the cap enforced by the `AdvancedSearch` panel. */
const MAX_ADVANCED_SEARCH = 5;

export const SubmissionPhase = ({
  currentState,
  isHoverDisabled
}: {
  currentState: string;
  isHoverDisabled?: boolean;
}) => {
  const phaseColumn = EDITOR_SUBMISSION_LIST_PHASE_MAP[currentState];

  return (
    <Flex alignItems="center" gap={2}>
      {phaseColumn?.icon ? (
        <Tooltip
          borderRadius={4}
          label={phaseColumn.description}
          shouldWrapChildren
          isDisabled={isHoverDisabled}
        >
          <Badge size="context" variant={phaseColumn.iconVariant}>
            <Flex gap={2} alignItems="center">
              <FontAwesomeIcon icon={phaseColumn.icon} />
              {phaseColumn?.alias ?? currentState}
            </Flex>
          </Badge>
        </Tooltip>
      ) : (
        <Badge size="context">{phaseColumn?.alias ?? currentState}</Badge>
      )}
    </Flex>
  );
};

const WorkspaceSubmissions = () => {
  const { shortname = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSaveFiltersModal, setShowSaveFiltersModal] =
    useState<boolean>(false);
  const [showManageFiltersModal, setShowManageFiltersModal] =
    useState<boolean>(false);
  const [filterName, setFilterName] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState(''); // Control state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [sort, setSort] = useState<SortField | null>(null);
  const [subBucket, setSubBucket] = useState('');
  const [isDynamicBucket, setIsDynamicBucket] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const toast = useToast();
  const { workspaceId } = useWorkspace();

  const view = 'submissions';
  const { data: dashBoardConfigs } = useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ view, workspaceId })
  });

  // Override the workflowPhase filter item names with the editor submission
  // list phase aliases so the alias is used consistently everywhere the filter
  // items are rendered (drawer, active-filter badges, saved-filter text).
  const dashBoardFilters = useMemo<
    Array<{ key: string; label: string; items: any[] }>
  >(
    () =>
      (dashBoardConfigs?.filters ?? []).map((filter: any) => {
        const items = Array.isArray(filter.items) ? filter.items : [];
        if (filter.key !== 'workflowPhase') {
          return { ...filter, items };
        }
        const mapItemName = (item: any) => ({
          ...item,
          name: EDITOR_SUBMISSION_LIST_PHASE_MAP[item.value]?.alias ?? item.name
        });
        return {
          ...filter,
          items: items.map((item: any) => ({
            ...mapItemName(item),
            items: Array.isArray(item.items)
              ? item.items.map(mapItemName)
              : undefined
          }))
        };
      }),
    [dashBoardConfigs?.filters]
  );

  const { watch, reset, getValues, setValue } =
    useForm<IWorkspaceSubmissionFilter>({
      defaultValues
    });

  const filters = watch();

  const advancedSearchFields = useMemo(
    () => dashBoardConfigs?.advancedSearchFields ?? [],
    [dashBoardConfigs?.advancedSearchFields]
  );

  const advancedSearchCriteria = useMemo<IAdvancedSearchCriterion[]>(
    () => filters.advancedSearch ?? [],
    [filters.advancedSearch]
  );

  // Criteria are stored as field names; label them for the active-filter chips.
  const advancedSearchLabelByField = useMemo(
    () =>
      new Map(
        advancedSearchFields.map(field => [field.value, field.label] as const)
      ),
    [advancedSearchFields]
  );

  const clearFilters = () => {
    reset(defaultValues);
    setSearchValue('');
    setSelectedFilters({});
    setSubBucket('');
    setShowAdvancedSearch(false);
    // Clear URL parameters
    navigate(location.pathname);
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        journalId: filters.journalId ?? [],
        tagId: filters.tagId ?? [],
        workflowPhase: filters.workflowPhase ?? [],
        documentType: filters.documentType ?? [],
        assigneeId: filters.assigneeId ?? [],
        focusIssueId: filters.focusIssueId ?? [],
        searchParams: (filters.searchParams ?? '').trim(),
        advancedSearch: filters.advancedSearch ?? []
      }),
    [
      filters.journalId,
      filters.tagId,
      filters.workflowPhase,
      filters.documentType,
      filters.assigneeId,
      filters.focusIssueId,
      filters.searchParams,
      filters.advancedSearch
    ]
  );
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  // Parse query parameters on initial load
  useEffect(() => {
    const { filters: newFilters, sort: newSort } = parseQueryParams(
      location.search
    );

    // Update state for search value
    setSearchValue(newFilters.searchParams);
    setSort(newSort);

    // Build selected filters with defaults for known keys
    const baseFilters = {
      tagId: newFilters?.tagId ?? [],
      workflowPhase:
        (newFilters?.workflowPhase as ISubmissionWorkflowState[])?.map(
          w => w
        ) ?? [],
      journalId: newFilters?.journalId ?? [],
      documentType: newFilters?.documentType ?? [],
      assigneeId: newFilters?.assigneeId ?? [],
      focusIssueId: newFilters?.focusIssueId ?? []
    };

    // Add any additional keys that aren’t in the known list. Advanced search is
    // held in its own panel rather than the filters drawer, so it is excluded
    // here — `selectedFilters` only tracks drawer groups.
    const knownKeys = [...Object.keys(baseFilters), 'advancedSearch'];
    const extraFilters = Object.keys(newFilters).reduce((acc, key) => {
      if (!knownKeys.includes(key)) {
        acc[key] = newFilters[key];
      }
      return acc;
    }, {} as Record<string, any>);

    // Merge both known and dynamic filters
    if ('subBucketName' in extraFilters) {
      setSubBucket(extraFilters.subBucketName);

      setSelectedFilters({});
    } else {
      setSelectedFilters({
        ...baseFilters,
        ...extraFilters
      });
    }
    if ('isDynamic' in extraFilters) {
      setIsDynamicBucket(extraFilters.isDynamic === 'true');
    } else {
      setIsDynamicBucket(false);
    }

    // `reset` sets a key to `undefined` when it is absent from the parsed
    // filters, so advanced search is normalised to keep the "always an array"
    // invariant the rest of the page relies on.
    reset({ ...newFilters, advancedSearch: newFilters.advancedSearch ?? [] });
    setShowAdvancedSearch(!!newFilters.advancedSearch?.length);
  }, [reset]);

  // Sync URL query params to formState
  useEffect(() => {
    updateQueryParams(
      {
        ...filters,
        sort: sort ? `${sort.label},${sort.value},${sort.direction}` : ''
      },
      location,
      navigate
    );
  }, [filters, sort, navigate]);
  const { data: manageTags } = useQuery({
    queryKey: ['workspace-tags', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceTags(workspaceId) : null)
  });
  const { data: savedFilters, isLoading: isSavedFiltersLoading } = useQuery({
    queryKey: ['submission-filter', { workspaceId }],
    queryFn: () => (workspaceId ? fetchSubmissionFilters(workspaceId) : null)
  });

  // The filters actually sent to the backend differ from the ones held in form
  // state: user tags are split out of `tagId`, and an empty `workflowPhase` is
  // expanded to every phase of the dashboard config. Both the list query and the
  // navigation state handed to the submission details page must use this exact
  // object, otherwise the "next submission" index would refer to a different
  // result set than the list the user clicked.
  const getEffectiveFilters = useCallback(
    (values: IWorkspaceSubmissionFilter): IWorkspaceSubmissionFilter => {
      const userTags = values.tagId.filter(tagId =>
        manageTags?.collection.some(
          allTag => tagId === allTag.id && allTag.usage === 'users'
        )
      );

      const submissionTags = values.tagId.filter(tagId =>
        manageTags?.collection.some(
          allTag => tagId === allTag.id && allTag.usage === 'submissions'
        )
      );

      const workflowPhaseFilter = dashBoardConfigs?.filters?.find(
        (filter: any) => filter.key === 'workflowPhase'
      ) as any;

      const workflowPhase = values.workflowPhase.length
        ? values.workflowPhase
        : workflowPhaseFilter && Array.isArray(workflowPhaseFilter.items)
        ? workflowPhaseFilter.items.map((item: any) => item.value)
        : [];

      return {
        ...values,
        tagId: submissionTags ?? [],
        userTags: userTags ?? [],
        workflowPhase
      };
    },
    [manageTags, dashBoardConfigs]
  );

  const offset = (page - 1) * pageSize;
  const { isLoading, isFetching: isFetchingSubmissions, data, isError } =
    useInfiniteQuery({
    retry: 0,
    queryKey: [
      'submission-list',
      {
        workspaceId,
        filters,
        page,
        pageSize,
        sort: sort !== null ? sort : undefined
      }
    ],
    enabled: !!dashBoardConfigs?.filters.length && !!workspaceId,
    queryFn: () =>
      fetchWorkspaceSubmissions({
        workspaceId,
        limit: pageSize,
        offset: offset,
        filters: getEffectiveFilters(filters),
        sort: sort ?? undefined
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const maxOffset = lastPage.total;
      const nextOffset = allPages.length * pageSize;
      return nextOffset < maxOffset ? nextOffset : undefined;
    }
  });

  const openItem = (
    itemId: string,
    submissionContextId: string,
    activeTab?: string,
    index?: number
  ) => {
    const submissionNav: SubmissionNavContext | undefined =
      typeof index === 'number'
        ? {
            index,
            // Only the current page is loaded, so its `total` is the size of the
            // whole filtered list.
            total: data?.pages?.[data.pages.length - 1]?.total,
            offset,
            limit: pageSize,
            workspaceId,
            filters: getEffectiveFilters(filters),
            sort: sort ?? null
          }
        : undefined;

    navigate(
      `/app/workspace/${shortname}/j/${submissionContextId}/submissions?selectedItem=${itemId}&activeTab=${
        activeTab || 'overview'
      }`,
      {
        state: {
          from: location.pathname + location.search,
          ...(submissionNav && { submissionNav })
        }
      }
    );
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: filtersItem
  ) => {
    const currentFilter = getValues(fieldName); // Get current tagId array
    const updatedFilter = currentFilter.filter(
      filter => filter !== filterToRemove
    ); // Remove the specific tag
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    }); // Update the field

    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setFilterName(null);
  };
  const handleSetValues = (data: ISavedFilterSets, name: string) => {
    setValue('searchParams', data?.searchParams ?? '');
    setValue('tagId', data?.tagId ?? []);
    setValue(
      'workflowPhase',
      (data?.workflowPhase as ISubmissionWorkflowState[]) ?? []
    );
    setValue('journalId', data?.journalId ?? []);
    setValue('documentType', data?.documentType ?? []);
    setValue('assigneeId', data?.assigneeId ?? []);
    setValue('focusIssueId', data?.focusIssueId ?? []);
    // The panel caps at 5 rows, so a filter saved with more than that (only
    // possible if the cap is ever raised) is truncated to match.
    const savedAdvancedSearch = data?.advancedSearch ?? [];
    setValue('advancedSearch', savedAdvancedSearch.slice(0, MAX_ADVANCED_SEARCH));
    setFilterName(name);
    setSearchValue(data?.searchParams ?? '');
    setShowAdvancedSearch(savedAdvancedSearch.length > 0);
    setSelectedFilters({
      tagId: data?.tagId ?? [],
      workflowPhase:
        (data?.workflowPhase as ISubmissionWorkflowState[])?.map(w => w) ?? [],
      journalId: data?.journalId ?? [],
      documentType: data?.documentType ?? [],
      assigneeId: data?.assigneeId ?? [],
      focusIssueId: data?.focusIssueId ?? []
    });
    return toast({
      variant: 'success',
      title: `${name} Filter Applied`
    });
  };

  const handleAdvancedSearchSubmit = (
    criteria: IAdvancedSearchCriterion[]
  ) => {
    setValue('advancedSearch', criteria, {
      shouldValidate: true,
      shouldDirty: true
    });
    setFilterName(null);
  };

  const handleAdvancedSearchReset = () => {
    setValue('advancedSearch', []);
    setFilterName(null);
  };

  const handleAdvancedSearchBack = () => {
    // Collapse only — the criteria stay applied and remain removable through
    // the active-filter chips.
    setShowAdvancedSearch(false);
  };

  const removeAdvancedSearchCriterion = (index: number) => {
    setValue(
      'advancedSearch',
      advancedSearchCriteria.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setFilterName(null);
  };
  const onDeleteClearFilters = () => {
    clearFilters();
    setFilterName(null);
  };
  const totalSubmissions = data?.pages?.[data.pages.length - 1]?.total ?? 0;
  const hasFilters =
    !!filters.workflowPhase.length ||
    !!filters.journalId.length ||
    !!filters.documentType.length ||
    !!filters.assigneeId.length ||
    !!filters?.tagId?.length ||
    !!filters?.focusIssueId?.length ||
    !!filters?.searchParams ||
    !!advancedSearchCriteria.length;
  const handleSaveFiltersModal = (name: string | null) => {
    setShowSaveFiltersModal(prev => !prev);
    if (name) setFilterName(name);
  };

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, totalSubmissions);

  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      const existingValues = getValues(key as string);
      const mergedValues =
        isDynamicBucket && Array.isArray(existingValues)
          ? Array.from(new Set([...(existingValues as string[]), ...values]))
          : values;

      setValue(key as string, mergedValues as any);
    });
  };

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      {showSaveFiltersModal ? (
        <SavedFiltersModal
          isOpen={showSaveFiltersModal}
          onClose={handleSaveFiltersModal}
          workspaceId={workspaceId}
          filter={filters}
        />
      ) : null}
      {showManageFiltersModal ? (
        <ManageFiltersModal
          isOpen={showManageFiltersModal}
          onClose={() => setShowManageFiltersModal(false)}
          workspaceId={workspaceId}
          savedFilters={savedFilters?.response}
          availableFilters={dashBoardFilters}
          isSavedFiltersLoading={isSavedFiltersLoading}
          clearFilters={onDeleteClearFilters}
          manageTags={manageTags ? manageTags.collection : []}
          advancedSearchFields={advancedSearchFields}
        />
      ) : null}
      <Box height="100%">
        <Box padding={{ base: 3, md: 5 }} minHeight="100%">
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="flex-start"
            gap={4}
          >
            <Flex flexDirection={'column'} gap={4} width="100%">
              <Flex flexDirection={'column'} gap={2} mb={2}>
                {/* ----- Search Input / Advanced Search ----- */}
                {dashBoardFilters.length > 0 && (
                  <Flex
                    gap={3}
                    width="100%"
                    alignItems={{ base: 'stretch', md: 'center' }}
                    flexDirection={{ base: 'column', md: 'row' }}
                  >
                    {showAdvancedSearch ? (
                      <Box width="100%">
                        <AdvancedSearch
                          fields={advancedSearchFields}
                          value={advancedSearchCriteria}
                          onChange={handleAdvancedSearchSubmit}
                          onReset={handleAdvancedSearchReset}
                          onBack={handleAdvancedSearchBack}
                          isLoading={isFetchingSubmissions}
                        />
                      </Box>
                    ) : (
                      <>
                        <Box width="100%">
                          <Flex gap={2} alignItems="center">
                            <SearchInput
                              placeholder="Search submissions by title or manuscript ID"
                              iconLeft={<FontAwesomeIcon icon={faSearch} />}
                              width="100%"
                              name="searchParams"
                              value={searchValue}
                              enterKeyHint="search"
                              onChange={e =>
                                setSearchValue(e.currentTarget.value)
                              }
                              onInputClear={() => {
                                setValue('searchParams', '');
                                setSearchValue('');
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  setValue(
                                    'searchParams',
                                    searchValue.trim()
                                  );
                                  setFilterName(null);
                                }
                              }}
                              onKeyUp={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  setValue(
                                    'searchParams',
                                    searchValue.trim()
                                  );
                                  setFilterName(null);
                                }
                              }}
                            />
                            <Box
                              display={{ base: 'block', lg: 'none' }}
                              flexShrink={0}
                            >
                              <Button
                                variant="primary"
                                aria-label="Search"
                                onClick={() => {
                                  setValue(
                                    'searchParams',
                                    searchValue.trim()
                                  );
                                  setFilterName(null);
                                }}
                              >
                                <FontAwesomeIcon icon={faSearch} />
                              </Button>
                            </Box>
                          </Flex>
                        </Box>
                        {advancedSearchFields.length > 0 && (
                          <Box
                            width={{ base: '100%', md: 'auto' }}
                            flexShrink={0}
                          >
                            <Button
                              variant="secondary"
                              iconLeft={<FontAwesomeIcon icon={faFilterList} />}
                              onClick={() => setShowAdvancedSearch(true)}
                              width={{ base: '100%', md: 'auto' }}
                            >
                              Advanced Search
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Flex>
                )}
              </Flex>
              <Flex
                alignItems={{ base: 'flex-start', md: 'center' }}
                gap={2}
                flexDirection={{ base: 'column', lg: 'row' }}
                justifyContent={'space-between'}
                width="100%"
              >
                <Flex gap={2} flexWrap="wrap" alignItems="center">
                  {/* Only the count and the save/clear actions depend on the
                      request, so the filter controls below stay usable while a
                      search is in flight. */}
                  {isLoading ? (
                    <Text>Searching for matches...</Text>
                  ) : (
                    <>
                      <Text
                        textStyle="textBody"
                        whiteSpace={{ base: 'normal', md: 'nowrap' }}
                      >
                        {totalSubmissions > 0 ? (
                          <>
                            Showing <strong>{startIndex}</strong> –{' '}
                            <strong>{endIndex}</strong> of{' '}
                            <strong>{totalSubmissions}</strong> submission
                            {totalSubmissions === 1 ? '' : 's'}
                          </>
                        ) : (
                          <>
                            <strong>0</strong> submissions
                          </>
                        )}
                      </Text>

                      {hasFilters && (
                        <>
                          {filterName ? (
                            <Text textStyle="textBody">
                              <strong>{filterName}</strong> applied
                            </Text>
                          ) : (
                            subBucket === '' && (
                              <ButtonText
                                leftIcon={
                                  <FontAwesomeIcon icon={faFloppyDisk} />
                                }
                                variant="secondary"
                                onClick={() => setShowSaveFiltersModal(true)}
                                marginX={{ base: 0, md: 2 }}
                              >
                                Save Filters
                              </ButtonText>
                            )
                          )}
                          {subBucket !== '' && (
                            <Text textStyle="textBody">
                              <strong>{subBucket}</strong> applied
                            </Text>
                          )}
                          <ButtonText
                            leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                            variant="secondary"
                            onClick={() => {
                              clearFilters();
                              setFilterName(null);
                            }}
                          >
                            Clear Filters
                          </ButtonText>
                        </>
                      )}
                    </>
                  )}
                </Flex>
                <Divider
                  orientation="horizontal"
                  flex="1"
                  mx={4}
                  borderColor="gray.300"
                  display={{ base: 'none', lg: 'block' }}
                />
                <Flex
                  gap={2}
                  alignItems="center"
                  flexWrap="wrap"
                  width={{ base: '100%', lg: 'auto' }}
                >
                  {dashBoardConfigs?.sortOptions && (
                    <Box width={{ base: '100%', sm: 'auto' }}>
                      <SortMenu
                        appliedSort={sort}
                        options={dashBoardConfigs.sortOptions}
                        onApply={handleSortApply}
                        onReset={handleSortReset}
                      />
                    </Box>
                  )}
                  {dashBoardFilters.length > 0 && (
                    <Box width={{ base: '100%', sm: 'auto' }}>
                      <FilterDrawer
                        filterGroups={dashBoardFilters}
                        watch={watch}
                        onSubmit={handleFiltersSubmit}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                      />
                    </Box>
                  )}
                  <Box width={{ base: '100%', sm: 'auto' }}>
                    <Menu ariaLabel="filters">
                      {({ onClose }) => (
                        <>
                          <MenuButton
                            as={Button}
                            variant="secondary"
                            iconLeft={<FontAwesomeIcon icon={faBarsFilter} />}
                            width={{ base: '100%', sm: 'auto' }}
                          >
                            Saved Filters
                          </MenuButton>
                          <MenuList
                            textStyle="headingItem"
                            minWidth={{
                              base: 'calc(100vw - 2rem)',
                              md: 'auto'
                            }}
                            maxWidth={{
                              base: 'calc(100vw - 2rem)',
                              md: '420px'
                            }}
                          >
                            {savedFilters?.response?.length ? (
                              <>
                                <Box maxH="300px" overflowY="auto" maxW="350px">
                                  {savedFilters.response.map(item => (
                                    <>
                                      <MenuItem
                                        onClick={() => {
                                          const filter =
                                            item.filters as ISavedFilterSets;
                                          handleSetValues(filter, item.name);
                                        }}
                                        ariaLabel={item.name}
                                        key={item.id}
                                      >
                                        <Text textStyle="headingLine" isTruncated>
                                          {item.name}
                                        </Text>
                                        {
                                          <SaveFilterText
                                            saveFilters={
                                              item.filters as ISavedFilterSets
                                            }
                                            availableFilters={dashBoardFilters}
                                            manageTags={
                                              manageTags
                                                ? manageTags.collection
                                                : []
                                            }
                                            advancedSearchFields={
                                              advancedSearchFields
                                            }
                                          />
                                        }
                                      </MenuItem>
                                      <MenuDivider />
                                    </>
                                  ))}
                                </Box>
                                <Flex
                                  alignItems="center"
                                  justifyContent="center"
                                  padding={3}
                                  position="sticky"
                                >
                                  <ButtonText
                                    onClick={() => {
                                      setShowManageFiltersModal(true);
                                      onClose();
                                    }}
                                  >
                                    Manage Filters
                                  </ButtonText>
                                </Flex>
                              </>
                            ) : (
                              <Flex
                                flexDirection={'column'}
                                gap={4}
                                padding={{ base: 4, md: 8 }}
                                justifyContent={'center'}
                                alignItems={'center'}
                                maxW="350px"
                              >
                                <Text textStyle="headingGroup">
                                  No Saved Filters
                                </Text>
                                <Text
                                  textStyle="textBody"
                                  textAlign={'center'}
                                >
                                  You do not have any saved filters yet. Try
                                  applying some filters and then click 'Save
                                  Filters' to save them for later usage.
                                </Text>
                              </Flex>
                            )}
                          </MenuList>
                        </>
                      )}
                    </Menu>
                  </Box>
                </Flex>
              </Flex>

              {!!hasFilters && (
                <Flex alignItems="center" gap={2} wrap="wrap">
                  {subBucket === '' &&
                    dashBoardFilters.map(filterConfig => {
                      // Type guard to check if filterConfig has 'items'
                      const hasItems = (fc: any): fc is { items: any[] } =>
                        Array.isArray(fc.items);

                      if (
                        !hasItems(filterConfig) ||
                        filterConfig.items.length === 0
                      ) {
                        return null;
                      }

                      const filterKey =
                        filterConfig.key as keyof typeof filters;
                      const filterValues = filters?.[filterKey];

                      if (
                        !Array.isArray(filterValues) ||
                        filterValues.length === 0
                      ) {
                        return null;
                      }

                      return filterValues.map(value => {
                        let item = filterConfig.items.find(
                          (i: any) => i.value === value
                        );
                        if (!item) {
                          for (const group of filterConfig.items) {
                            if (Array.isArray(group.items)) {
                              const child = group.items.find(
                                (c: any) => c.value === value
                              );
                              if (child) {
                                item = child;
                                break;
                              }
                            }
                          }
                        }

                        const label = item?.name ?? value;

                        return (
                          <Badge
                            key={`${filterKey}-${value}`}
                            size="base"
                            variant="interactive"
                          >
                            <Flex alignItems="center" gap={2}>
                              {label}
                              <FontAwesomeIcon
                                icon={faXmark}
                                cursor="pointer"
                                onClick={() =>
                                  removeSingleFilter(
                                    value,
                                    filterKey as filtersItem
                                  )
                                }
                              />
                            </Flex>
                          </Badge>
                        );
                      });
                    })}

                  {advancedSearchCriteria.map((criterion, index) => (
                    <Badge
                      key={`advancedSearch-${criterion.field}-${index}`}
                      size="base"
                      variant="interactive"
                    >
                      <Flex alignItems="center" gap={2}>
                        {advancedSearchLabelByField.get(criterion.field) ??
                          criterion.field}
                        : {formatCriterionValue(criterion.value)}
                        <FontAwesomeIcon
                          icon={faXmark}
                          cursor="pointer"
                          onClick={() => removeAdvancedSearchCriterion(index)}
                        />
                      </Flex>
                    </Badge>
                  ))}
                </Flex>
              )}
            </Flex>
            <Box width="100%">
              {isLoading ? (
                <SubmissionLoader
                  title="Loading Search Results"
                  subTitle="We are retrieving your search results. This should only take a moment."
                  bgColor="neutralSecondaryFill"
                />
              ) : data?.pages?.every(page => page?.items?.length !== 0) ? (
                <Box overflowY={'auto'} overflowX="auto" width="100%">
                  {/* @ts-ignore */}
                  <Table size={'sm'} width="1010px" minWidth="100%">
                    <Thead>
                      <Tr
                        data-testId="submissions-table-header"
                        gap={4}
                        sx={{
                          '& th': {
                            textTransform: 'none',
                            color: 'black',
                            background: 'white'
                          }
                        }}
                      >
                        <Th>
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Submission Title & Authors
                          </Text>
                        </Th>
                        <Th>
                          <Text textStyle="headingItem">Submission Date</Text>
                        </Th>

                        <Th>
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Assigned to
                          </Text>
                        </Th>
                        <Th>
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Activity
                          </Text>
                        </Th>
                        <Th>
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Phase/Recommendation
                          </Text>
                        </Th>
                        <Th>
                          <Text
                            textStyle="headingItem"
                            whiteSpace="nowrap"
                          ></Text>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data?.pages.map(page =>
                        page?.items.map(
                          (
                            {
                              submission: {
                                title,
                                documentType,
                                authors,
                                isUADDetected
                              },
                              id,
                              submissionContextId,
                              workflow: {
                                currentState,
                                dateEnteredCurrentPhase
                              },
                              recommendationAssignees,
                              tags,
                              manuscriptId,
                              createdAt,
                              recommendationWorkflowData,
                              peerReview
                            },
                            rowIndex
                          ) => {
                            // The list is paginated via the query key rather than
                            // fetchNextPage, so `data.pages` always holds one page
                            // and a row's absolute index is simply offset + row.
                            const absoluteIndex = offset + rowIndex;
                            return (
                              <Tr
                                key={id}
                                data-testId="submissions-table-row"
                                gap={4}
                                background="white"
                              >
                                <Td
                                  onClick={() =>
                                    openItem(
                                      id,
                                      submissionContextId,
                                      undefined,
                                      absoluteIndex
                                    )
                                  }
                                  cursor="pointer"
                                  _hover={{ bg: 'gray.50' }}
                                >
                                  <ClearButton
                                    width="100%"
                                    ariaLabel={`Open submission ${title}`}
                                    data-testId="submissions-row-title"
                                  >
                                    <Flex
                                      direction="column"
                                      alignItems="flex-start"
                                      justifyContent="flex-start"
                                      width="100%"
                                      minWidth={0}
                                      textAlign="left"
                                    >
                                      <Tooltip
                                        hasArrow
                                        borderRadius={4}
                                        placement="top"
                                        label={'View Submission'}
                                      >
                                        <Box width="100%">
                                          <Flex align="center" gap={1}>
                                            <Text textStyle="headingLine">
                                              {manuscriptId}
                                            </Text>
                                            <Text>|</Text>
                                            <Text textStyle="textBody">
                                              {SUBMISSION_TYPE_MAP[
                                                documentType as IDocumentType
                                              ] ?? documentType}
                                            </Text>
                                          </Flex>
                                        </Box>

                                        <Box
                                          noOfLines={2}
                                          isTruncated
                                          width="100%"
                                          cursor="pointer"
                                        >
                                          <Text
                                            textStyle="textBody"
                                            whiteSpace="normal"
                                            textDecoration="none"
                                            _hover={{
                                              textDecoration: 'underline',
                                              textDecorationColor:
                                                'actionPrimaryFill'
                                            }}
                                          >
                                            <RichTextPreview
                                              value={title}
                                              color="actionPrimaryFill"
                                            />
                                          </Text>
                                        </Box>
                                      </Tooltip>

                                      <SubmissionAuthors
                                        authors={authors}
                                        isViewModeActive={false}
                                      />
                                    </Flex>
                                  </ClearButton>
                                  <SubmissionTags id={id} tags={tags} />
                                </Td>
                                <Td textStyle="textContext">
                                  {createdAt !== null
                                    ? formatDate(new Date(createdAt ?? 'N/A'))
                                    : 'N/A'}
                                </Td>

                                <Td textStyle="textBody">
                                  {Object.entries(
                                    (recommendationAssignees ?? []).reduce<
                                      Record<
                                        string,
                                        typeof recommendationAssignees
                                      >
                                    >((assigneesByRole, assignee) => {
                                      (assigneesByRole[assignee.role] ??=
                                        []).push(assignee);
                                      return assigneesByRole;
                                    }, {})
                                  ).map(([role, assignees], index) => (
                                    <Flex
                                      key={role}
                                      gap={1}
                                      alignItems="flex-start"
                                      width="100%"
                                    >
                                      <Text
                                        whiteSpace="nowrap"
                                        textStyle="headingItem"
                                        flexShrink={0}
                                      >
                                        {role}:
                                      </Text>
                                      <Flex gap={1} flexWrap="wrap" minW={0}>
                                        {assignees?.map(assignee => (
                                          <Flex
                                            key={assignee?.id}
                                            alignItems="center"
                                            gap={1}
                                            whiteSpace="nowrap"
                                          >
                                            <Box
                                              width={2}
                                              height={2}
                                              borderRadius="full"
                                              backgroundColor={
                                                avatarBackgroundByRoleLevel[
                                                  index + 1
                                                ]
                                              }
                                            />
                                            <Text
                                              textStyle="textBody"
                                              whiteSpace="nowrap"
                                            >
                                              {assignee.firstName}{' '}
                                              {assignee.lastName}
                                            </Text>
                                          </Flex>
                                        ))}
                                      </Flex>
                                    </Flex>
                                  ))}
                                </Td>

                                <Td data-testId="submission-peer-review">
                                  {peerReview ? (
                                    <Flex direction="column">
                                      {(
                                        [
                                          ['Invited', peerReview.invited],
                                          ['Agreed', peerReview.agreed],
                                          ['Declined', peerReview.declined],
                                          [
                                            'No Response',
                                            peerReview.noResponse
                                          ],
                                          ['Completed', peerReview.completed]
                                        ] as const
                                      )
                                        .filter(([, count]) => (count ?? 0) > 0)
                                        .map(([label, count]) => (
                                          <Text
                                            key={label}
                                            textStyle="textContext"
                                            whiteSpace="nowrap"
                                          >
                                            {label}: {count}
                                          </Text>
                                        ))}
                                    </Flex>
                                  ) : null}
                                </Td>

                                <Td data-testId="submission-phase">
                                  <Flex alignItems={'center'} gap={1}>
                                    <SubmissionPhase
                                      currentState={currentState}
                                    />{' '}
                                    {isUADDetected && (
                                      <Tooltip
                                        label={
                                          'Unusual activities are detected for this submission'
                                        }
                                        hasArrow
                                        placement="top"
                                      >
                                        {' '}
                                        <Box>
                                          <AnomalyDetectionIcon />{' '}
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Flex>
                                  <Text textStyle="textContext">
                                    since{' '}
                                    {dateEnteredCurrentPhase
                                      ? formatDate(
                                          new Date(dateEnteredCurrentPhase!)
                                        )
                                      : null}
                                  </Text>
                                  {recommendationWorkflowData?.currentState ===
                                  'awaitingRecommendation' ? (
                                    <Text textStyle="textContext" mt={2}>
                                      Awaiting{' '}
                                      {recommendationWorkflowData.awaitingRole}{' '}
                                      Recommendation
                                    </Text>
                                  ) : recommendationWorkflowData?.currentState ===
                                    'awaitingAssignment' ? (
                                    <Text textStyle="textContext" mt={2}>
                                      Awaiting{' '}
                                      {recommendationWorkflowData.awaitingRole}{' '}
                                      Assignment
                                    </Text>
                                  ) : recommendationWorkflowData?.currentState ===
                                    'recommendationFinished' ? (
                                    <Text textStyle="textContext" mt={2}>
                                      {recommendationWorkflowData.awaitingRole}{' '}
                                      Recommendation Complete
                                    </Text>
                                  ) : recommendationWorkflowData?.currentState ===
                                    'awaitingEditorAssignment' ? (
                                    <Text textStyle="textContext" mt={2}>
                                      Awaiting Editor Assignment
                                    </Text>
                                  ) : null}
                                </Td>
                                <Td textStyle="textBody">
                                  <Flex justifyContent={'flex-end'}>
                                    <SubmissionActionMenu
                                      submissionId={id}
                                      submissionContextId={submissionContextId}
                                      handleClick={(
                                        id,
                                        submissionContextId,
                                        activeTab
                                      ) =>
                                        openItem(
                                          id,
                                          submissionContextId ?? '',
                                          activeTab,
                                          absoluteIndex
                                        )
                                      }
                                    />
                                  </Flex>
                                </Td>
                              </Tr>
                            );
                          }
                        )
                      )}
                    </Tbody>
                  </Table>
                </Box>
              ) : isError ? (
                <>
                  <SubmissionLoader
                    title="Search Failed"
                    subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
                    bgColor="errorPrimaryFill"
                  />
                </>
              ) : (
                <SubmissionLoader
                  title="No Results Found"
                  subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
                  bgColor="neutralSecondaryFill"
                />
              )}
              <TablePagination
                ariaLabel="Submission pagination"
                currentPage={page}
                totalItems={totalSubmissions}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </Box>
          </Flex>
        </Box>
        <Footer />
      </Box>
      <ScrollToTopButton />
    </Box>
  );
};

export default WorkspaceSubmissions;
